import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import AdminLayout from "./AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLessons() {
  const { getAuthHeader, token } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState("");
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    package_id: "",
    order_number: 1,
    grammar_topic: "",
    description: "",
    topics: "",
    vocabulary: ""
  });

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  useEffect(() => {
    if (selectedPackage && token) {
      fetchLessons(selectedPackage);
    }
  }, [selectedPackage, token]);

  const fetchData = async () => {
    try {
      const packagesRes = await axios.get(`${API}/admin/packages`, getAuthHeader());
      setPackages(packagesRes.data.filter(p => p.is_active));
      if (packagesRes.data.length > 0) {
        setSelectedPackage(packagesRes.data[0].id);
      }
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const fetchLessons = async (packageId) => {
    try {
      const response = await axios.get(
        `${API}/admin/lessons?package_id=${packageId}`,
        getAuthHeader()
      );
      setLessons(response.data);
    } catch (error) {
      console.error("Error fetching lessons:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingLesson) {
        await axios.put(
          `${API}/admin/lessons/${editingLesson.id}`,
          formData,
          getAuthHeader()
        );
        toast.success("Ders güncellendi");
      } else {
        await axios.post(`${API}/admin/lessons`, formData, getAuthHeader());
        toast.success("Ders oluşturuldu");
      }
      fetchLessons(selectedPackage);
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (lesson) => {
    setEditingLesson(lesson);
    setFormData({
      package_id: lesson.package_id,
      order_number: lesson.order_number,
      grammar_topic: lesson.grammar_topic,
      description: lesson.description,
      topics: lesson.topics || "",
      vocabulary: lesson.vocabulary || ""
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu dersi silmek istediğinize emin misiniz?")) return;

    try {
      await axios.delete(`${API}/admin/lessons/${id}`, getAuthHeader());
      toast.success("Ders silindi");
      fetchLessons(selectedPackage);
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const resetForm = () => {
    setEditingLesson(null);
    setFormData({
      package_id: selectedPackage,
      order_number: lessons.length + 1,
      grammar_topic: "",
      description: "",
      topics: "",
      vocabulary: ""
    });
  };

  const openNewDialog = () => {
    resetForm();
    setFormData(prev => ({ ...prev, package_id: selectedPackage }));
    setIsDialogOpen(true);
  };

  return (
    <AdminLayout title="Dersler/Oturumlar">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <Label className="whitespace-nowrap text-neutral-300">Paket Seç:</Label>
            <Select value={selectedPackage} onValueChange={setSelectedPackage}>
              <SelectTrigger className="w-64 bg-neutral-800 border-neutral-700 text-white" data-testid="lesson-package-select">
                <SelectValue placeholder="Paket seçin" />
              </SelectTrigger>
              <SelectContent className="bg-neutral-800 border-neutral-700">
                {packages.map((pkg) => (
                  <SelectItem key={pkg.id} value={pkg.id} className="text-white">{pkg.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-red-600 hover:bg-red-700" onClick={openNewDialog} data-testid="add-lesson-btn">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Ders
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg bg-neutral-900 border-neutral-800 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white font-['Nunito']">
                  {editingLesson ? "Ders Düzenle" : "Yeni Ders Oluştur"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-neutral-300">Paket</Label>
                  <Select 
                    value={formData.package_id} 
                    onValueChange={(v) => setFormData({ ...formData, package_id: v })}
                  >
                    <SelectTrigger className="bg-neutral-800 border-neutral-700 text-white">
                      <SelectValue placeholder="Paket seçin" />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-800 border-neutral-700">
                      {packages.map((pkg) => (
                        <SelectItem key={pkg.id} value={pkg.id} className="text-white">{pkg.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-neutral-300">Sıra No</Label>
                  <Input
                    type="number"
                    value={formData.order_number}
                    onChange={(e) => setFormData({ ...formData, order_number: parseInt(e.target.value) })}
                    min="1"
                    required
                    className="bg-neutral-800 border-neutral-700 text-white"
                    data-testid="lesson-order-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-neutral-300">Gramer Konusu</Label>
                  <Input
                    value={formData.grammar_topic}
                    onChange={(e) => setFormData({ ...formData, grammar_topic: e.target.value })}
                    placeholder="Örn: Simple Present Tense"
                    required
                    className="bg-neutral-800 border-neutral-700 text-white"
                    data-testid="lesson-topic-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-neutral-300">Açıklama</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Ders açıklaması"
                    required
                    className="bg-neutral-800 border-neutral-700 text-white"
                    data-testid="lesson-desc-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-neutral-300">Konular (Neler hakkında konuşulacak?)</Label>
                  <Textarea
                    value={formData.topics}
                    onChange={(e) => setFormData({ ...formData, topics: e.target.value })}
                    placeholder="Örn: Daily routines, Family, Hobbies, School life"
                    className="bg-neutral-800 border-neutral-700 text-white min-h-[80px]"
                    data-testid="lesson-topics-input"
                  />
                  <p className="text-xs text-neutral-500">AI sadece bu konularda soru soracak</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-neutral-300">Kelime Listesi (Opsiyonel)</Label>
                  <Textarea
                    value={formData.vocabulary}
                    onChange={(e) => setFormData({ ...formData, vocabulary: e.target.value })}
                    placeholder="Örn: wake up, go to school, have breakfast, watch TV"
                    className="bg-neutral-800 border-neutral-700 text-white min-h-[80px]"
                    data-testid="lesson-vocabulary-input"
                  />
                  <p className="text-xs text-neutral-500">AI bu kelimeleri kullanacak ve ilgili kelimelerle genişletecek</p>
                </div>
                <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={saving} data-testid="save-lesson-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingLesson ? "Güncelle" : "Oluştur"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Lessons Table */}
        <Card className="border-neutral-800 bg-neutral-900">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
              </div>
            ) : lessons.length === 0 ? (
              <div className="text-center py-12 text-neutral-500">
                Bu pakete ait ders bulunmuyor.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-neutral-800">
                    <TableHead className="w-16 text-neutral-400">Sıra</TableHead>
                    <TableHead className="text-neutral-400">Gramer Konusu</TableHead>
                    <TableHead className="text-neutral-400">Konular</TableHead>
                    <TableHead className="w-24 text-neutral-400">İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lessons.map((lesson) => (
                    <TableRow key={lesson.id} className="border-neutral-800" data-testid={`lesson-row-${lesson.id}`}>
                      <TableCell className="font-medium text-white">{lesson.order_number}</TableCell>
                      <TableCell className="text-white">{lesson.grammar_topic}</TableCell>
                      <TableCell className="text-neutral-400 max-w-xs truncate">{lesson.topics || "-"}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="hover:bg-neutral-800" onClick={() => handleEdit(lesson)}>
                            <Edit className="w-4 h-4 text-neutral-400" />
                          </Button>
                          <Button variant="ghost" size="icon" className="hover:bg-neutral-800" onClick={() => handleDelete(lesson.id)}>
                            <Trash2 className="w-4 h-4 text-neutral-400" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
